import { useCallback, useEffect, useState } from 'react'
import { useAuth } from '@structural-climbing/auth'

export interface TrainerAssignment {
  id: string
  trainer_user_id: string
  trainer_name: string | null
  /** The account the assignment hangs off; the parent's, for a child. */
  member_user_id: string
  child_id: string | null
  child_name: string | null
  starts_at: string
  ends_at: string | null
  ended_at: string | null
  end_reason: string | null
  note: string | null
  /** Live and inside its date window today, in the gym's timezone. */
  is_current: boolean
  /** Where it sits relative to today: not started, running, or over. */
  status: 'upcoming' | 'current' | 'past'
}

/**
 * One member's coaching history: current assignments first, ended ones kept so
 * a reassignment reads as a chain rather than an overwrite.
 */
export function useTrainerAssignments(memberUserId: string | null) {
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string

  const [assignments, setAssignments] = useState<TrainerAssignment[]>([])
  const [loading, setLoading] = useState(false)

  const reload = useCallback(async () => {
    if (!memberUserId) return
    setLoading(true)
    try {
      const token = await getAccessTokenSilently()
      const resp = await fetch(
        `${apiUrl}/trainer-assignments?member_user_id=${encodeURIComponent(memberUserId)}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      if (!resp.ok) throw new Error(String(resp.status))
      const data = (await resp.json()) as { assignments: TrainerAssignment[] }
      setAssignments(data.assignments ?? [])
    } catch {
      setAssignments([])
    } finally {
      setLoading(false)
    }
  }, [apiUrl, getAccessTokenSilently, memberUserId])

  useEffect(() => {
    void reload()
  }, [reload])

  return { assignments, loading, reload }
}
